/**
 * Utility functions for calculating market status from instrument trading hours
 */

export interface TradingHour {
    days: number[]; // 0 = Sunday, 1 = Monday, etc.
    open: string; // Format: "HH:MM"
    close: string; // Format: "HH:MM"
}

export interface MarketStatus {
    is_trading_open: boolean;
    status_text: string;
    next_opening?: string;
    next_closing?: string;
    last_update: string;
    market_info?: { type?: string };
}

/**
 * Calculate market status from instrument trading hours
 */
export function calculateMarketStatus(tradingHours: TradingHour[]): MarketStatus {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.getTime();

    // Find the current trading session
    const currentSession = tradingHours.find(session => session.days.includes(currentDay));

    if (!currentSession) {
        // Market is closed today, find next opening
        const nextOpening = findNextOpening(tradingHours, now);
        return {
            is_trading_open: false,
            status_text: 'Market Closed',
            next_opening: nextOpening,
            last_update: now.toISOString(),
        };
    }

    // Parse current session hours
    const [openHour, openMinute] = currentSession.open.split(':').map(Number);
    const [closeHour, closeMinute] = currentSession.close.split(':').map(Number);

    const openTime = new Date(now);
    openTime.setHours(openHour, openMinute, 0, 0);

    const closeTime = new Date(now);
    closeTime.setHours(closeHour, closeMinute, 0, 0);

    // Handle overnight sessions (e.g., close at 23:00, open at 01:00)
    if (closeTime <= openTime) {
        closeTime.setDate(closeTime.getDate() + 1);
    }

    const isOpen = currentTime >= openTime.getTime() && currentTime < closeTime.getTime();

    if (isOpen) {
        return {
            is_trading_open: true,
            status_text: 'Market Open',
            next_closing: closeTime.toISOString(),
            last_update: now.toISOString(),
        };
    } else {
        // Market is closed, find next opening
        const nextOpening = findNextOpening(tradingHours, now);
        return {
            is_trading_open: false,
            status_text: 'Market Closed',
            next_opening: nextOpening,
            last_update: now.toISOString(),
        };
    }
}

/**
 * Find the next market opening time
 */
function findNextOpening(tradingHours: TradingHour[], fromDate: Date): string {
    const sortedSessions = [...tradingHours].sort((a, b) => {
        const aMinDay = Math.min(...a.days);
        const bMinDay = Math.min(...b.days);
        return aMinDay - bMinDay;
    });

    // Look for next opening in the next 7 days
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const checkDate = new Date(fromDate);
        checkDate.setDate(checkDate.getDate() + dayOffset);
        const checkDay = checkDate.getDay();

        const session = sortedSessions.find(s => s.days.includes(checkDay));
        if (session) {
            const [openHour, openMinute] = session.open.split(':').map(Number);
            const nextOpen = new Date(checkDate);
            nextOpen.setHours(openHour, openMinute, 0, 0);

            // Only return if it's in the future
            if (nextOpen.getTime() > fromDate.getTime()) {
                return nextOpen.toISOString();
            }
        }
    }

    // Fallback: return next Monday at 9:30 AM
    const nextMonday = new Date(fromDate);
    nextMonday.setDate(nextMonday.getDate() + ((8 - nextMonday.getDay()) % 7));
    nextMonday.setHours(9, 30, 0, 0);
    return nextMonday.toISOString();
}

/**
 * Get market status for an instrument with trading hours
 */
export function getInstrumentMarketStatus(instrument: any): MarketStatus {
    if (instrument?.is_within_trading_hours !== undefined) {
        return {
            is_trading_open: instrument.is_within_trading_hours,
            status_text: instrument.is_within_trading_hours ? 'Market Open' : 'Market Closed',
            last_update: new Date().toISOString(),
        };
    }

    if (!instrument?.trading_hours || !Array.isArray(instrument.trading_hours)) {
        return {
            is_trading_open: false,
            status_text: 'Market Closed',
            last_update: new Date().toISOString(),
        };
    }

    return calculateMarketStatus(instrument.trading_hours);
}

/**
 * Get market status text with more context
 */
export function getMarketStatusText(marketStatus: MarketStatus): string {
    if (marketStatus.is_trading_open) {
        return marketStatus.status_text;
    }

    if (marketStatus.next_opening) {
        const nextOpen = new Date(marketStatus.next_opening);
        const now = new Date();
        const diffMs = nextOpen.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffHours > 0) {
            return `Market Closed - Opens in ${diffHours}h ${diffMinutes}m`;
        } else if (diffMinutes > 0) {
            return `Market Closed - Opens in ${diffMinutes}m`;
        } else {
            return 'Market Closed - Opens Soon';
        }
    }

    return marketStatus.status_text;
}
